import { useApi } from '@backstage/core-plugin-api';
import { uptimeMonitorApiRef } from '../api/UptimeMonitorApi';
import { useAsyncFn } from 'react-use';
import { useEffect, useCallback } from 'react';
import { IssueCommentsRequest, Comment, AddCommentRequest, UpdateCommentRequest, DeleteCommentRequest } from '../types';

export const useIssueComments = (params: IssueCommentsRequest | null) => {
  const api = useApi(uptimeMonitorApiRef);

  const [state, fetch] = useAsyncFn(async (): Promise<Comment[]> => {
    if (!params) return [];
    
    try {
      console.log('Fetching issue comments for:', params);
      const result = await api.getIssueComments(params);
      console.log('Issue comments result:', result);
      
      return result.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
    } catch (err: any) {
      console.error('Error fetching issue comments:', err);
      throw err;
    }
  }, [params?.host, params?.id]);

  // Add comment
  const [addState, addComment] = useAsyncFn(async (text: string): Promise<Comment> => {
    if (!params) throw new Error('No issue params provided');
    
    const addParams: AddCommentRequest = {
      host: params.host,
      id: params.id,
      text,
    };
    
    const result = await api.addIssueComment(addParams);
    fetch();
    
    return result;
  }, [params, api, fetch]);

  // Update comment
  const [updateState, updateComment] = useAsyncFn(async (commentId: number, text: string): Promise<Comment> => {
    if (!params) throw new Error('No issue params provided');
    
    const updateParams: UpdateCommentRequest = {
      host: params.host,
      id: params.id,
      commentId,
      text,
    };
    
    const result = await api.updateIssueComment(updateParams);
    fetch();
    
    return result;
  }, [params, api, fetch]);

  // Delete comment
  const [deleteState, deleteComment] = useAsyncFn(async (commentId: number): Promise<void> => {
    if (!params) throw new Error('No issue params provided');
    
    const deleteParams: DeleteCommentRequest = {
      host: params.host,
      id: params.id,
      commentId,
    };
    
    await api.deleteIssueComment(deleteParams);
    
    fetch();
  }, [params, api, fetch]);

  // Auto-fetch when params change
  useEffect(() => {
    if (params) {
      fetch();
    }
  }, [params?.host, params?.id, fetch]);

  // Refresh function for external use
  const refetch = useCallback(() => {
    fetch();
  }, [fetch]);

  return {
    // Comments data
    comments: state.value || [],
    isLoading: state.loading,
    error: state.error,
    
    // Actions
    addComment,
    updateComment,
    deleteComment,
    refetch,
    
    // Action states
    isAdding: addState.loading,
    addError: addState.error,
    isUpdating: updateState.loading,
    updateError: updateState.error,
    isDeleting: deleteState.loading,
    deleteError: deleteState.error,
  };
};